import { useState, useEffect, useCallback, useRef } from 'react';
import { supabaseService, ScanStats } from '../services/supabaseService';
import { useAuth } from './useAuth';
import { useToast } from './useToast';
import { statsEmitter } from '../utils/statsEmitter';

export const useStats = () => {
  const [stats, setStats] = useState<ScanStats>({
    totalScans: 0,
    savedItems: 0,
    thisMonth: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const loadingRef = useRef(false); // Prevent concurrent requests
  const cacheRef = useRef<{ data: ScanStats; timestamp: number } | null>(null);
  
  const CACHE_DURATION = 30000; // 30 seconds cache

  const loadStats = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('Stats loading already in progress, skipping...');
      return;
    }

    // Check cache first
    if (!forceRefresh && cacheRef.current) {
      const now = Date.now();
      const cacheAge = now - cacheRef.current.timestamp;
      if (cacheAge < CACHE_DURATION) {
        console.log('Using cached stats:', cacheRef.current.data);
        setStats(cacheRef.current.data);
        setIsLoading(false);
        return;
      }
    }

    try {
      loadingRef.current = true;
      setIsLoading(true);
      console.log('Loading fresh stats...');
      
      if (!user?.id) {
        console.log('No user ID available');
        const fallbackStats = {
          totalScans: 0,
          savedItems: 0,
          thisMonth: 0,
        };
        setStats(fallbackStats);
        cacheRef.current = { data: fallbackStats, timestamp: Date.now() };
        return;
      }
      
      const userStats = await supabaseService.getUserStats(user.id);
      console.log('Fresh stats loaded from useStats:', userStats);
      console.log('User ID:', user.id);
      setStats(userStats);
      cacheRef.current = { data: userStats, timestamp: Date.now() };
    } catch (error) {
      console.error('Failed to load stats:', error);
      
      // Check for specific error types
      if (error instanceof Error) {
        if (error.message.includes('permission denied') || error.message.includes('42501')) {
          console.log('Permission denied - database access issue detected');
          showToast('warning', 'Database Setup Required', 'Please check database permissions');
        } else if (error.message.includes('403')) {
          console.log('Permission denied - using fallback stats');
        } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
          console.log('Database server error - using fallback stats');
        }
      }
      
      // Use fallback stats
      const fallbackStats = {
        totalScans: 0,
        savedItems: 0,
        thisMonth: 0,
      };
      setStats(fallbackStats);
      cacheRef.current = { data: fallbackStats, timestamp: Date.now() };
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [user?.id, showToast]);

  const refreshStats = useCallback(() => {
    console.log('🔄 Manual stats refresh requested');
    cacheRef.current = null; // Clear cache
    loadStats(true); // Force refresh
  }, [loadStats]);

  // Load stats only when authentication is ready and user exists
  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.id) {
      loadStats();
    } else if (!authLoading && !isAuthenticated) {
      // Clear stats when not authenticated
      setStats({
        totalScans: 0,
        savedItems: 0,
        thisMonth: 0,
      });
      setIsLoading(false);
      cacheRef.current = null;
    }
  }, [isAuthenticated, authLoading, user?.id, loadStats]);

  // Listen for stats refresh events with debouncing
  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.id) {
      let timeoutId: NodeJS.Timeout;
      
      const debouncedRefresh = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          loadStats(true);
        }, 500); // 500ms debounce
      };
      
      const unsubscribe = statsEmitter.subscribe(debouncedRefresh);
      
      return () => {
        clearTimeout(timeoutId);
        unsubscribe();
      };
    }
  }, [isAuthenticated, authLoading, user?.id, loadStats]);

  return {
    stats,
    isLoading,
    refreshStats,
  };
};
